import { useState, useRef, useEffect } from 'react';
import styles from './VariantDropdown.module.scss';
import cx from 'classnames';
import { apiGetPdpProductsVariants } from '../../helpers/api';
import { ADMIN_MY_PRODUCTS_3DMODAL_EACH } from '../../constants/routePathConstants';
import { navigate } from '../../helpers/projectHelper';
import { ElipsisTooltip } from '../../common/ElipsisTooltip';
import { VariantIcon, ChevronDownIcon, ArrowLeftIcon, ArrowRightIcon } from '../../assets/icons';
import Image from '../../common/Image';

const VISIBLE_COUNT = 3;

const VariantDropdown = ({ productDetails }) => {
  const {
    owner_organization,
    base_model,
    name: productName,
    id: currentProductId,
  } = productDetails || {};
  const [isOpen, setIsOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [variants, setVariants] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!owner_organization || !base_model) return;

    const fetchVariants = async () => {
      const result = await apiGetPdpProductsVariants({ brands: owner_organization, base_model });
      if (result?.results) {
        setVariants(result.results);
        const currentIndex = result.results.findIndex((v) => v.id === currentProductId);
        if (currentIndex > 0) {
          setStartIndex(Math.floor(currentIndex / VISIBLE_COUNT) * VISIBLE_COUNT);
        }
      }
    };

    fetchVariants();
  }, [owner_organization, base_model]);

  const currentVariant = variants.find((v) => v.id === currentProductId) || variants[0];
  const displayName = currentVariant?.name || productName || 'Variant';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVariantClick = async (variant) => {
    if (variant.id === currentProductId) return;
    const url = ADMIN_MY_PRODUCTS_3DMODAL_EACH.replace(':id', variant.id);
    navigate(url);
    setIsOpen(false);
  };

  const handlePrev = () => {
    setStartIndex((prev) => Math.max(0, prev - VISIBLE_COUNT));
  };

  const handleNext = () => {
    setStartIndex((prev) => prev + VISIBLE_COUNT);
  };

  const visibleVariants = variants.slice(startIndex, startIndex + VISIBLE_COUNT);
  const canGoPrev = startIndex > 0;
  const canGoNext = startIndex + VISIBLE_COUNT < variants.length;

  if (!variants?.length) return null;

  return (
    <div className="position-relative d-inline-block" ref={dropdownRef}>
      <ElipsisTooltip title={displayName} alwaysShow>
        {(props) => (
          <button
            className={cx('d-flex align-items-center', styles.triggerButton, {
              [styles.active]: isOpen,
            })}
            {...props}
            onClick={() => setIsOpen(!isOpen)}>
            <VariantIcon color="#E87B35" />
            <span className={`${styles.triggerText} text-truncate`}>
              Variant: <strong>{displayName}</strong>
            </span>
            <ChevronDownIcon />
          </button>
        )}
      </ElipsisTooltip>

      {isOpen && (
        <div className={styles.dropdownPanel}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className={styles.headerTitle}>SELECT VARIANT</span>
            {(canGoPrev || canGoNext) && (
              <div className="d-flex gap-1">
                <button
                  className={cx(
                    'd-flex align-items-center justify-content-center',
                    styles.arrowBtn
                  )}
                  onClick={handlePrev}
                  disabled={!canGoPrev}>
                  <ArrowLeftIcon />
                </button>
                <button
                  className={cx(
                    'd-flex align-items-center justify-content-center',
                    styles.arrowBtn
                  )}
                  onClick={handleNext}
                  disabled={!canGoNext}>
                  <ArrowRightIcon />
                </button>
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            {visibleVariants.map((variant) => (
              <ElipsisTooltip key={variant.id} title={variant.name} alwaysShow>
                {(props) => (
                  <button
                    className={cx('flex-shrink-0 p-0', styles.variantItem, {
                      [styles.selected]: variant.id === currentProductId,
                    })}
                    {...props}
                    onClick={() => handleVariantClick(variant)}>
                    <Image
                      src={variant.thumbnail}
                      alt={variant.name}
                      ratio={1}
                      placeholderWhileLoading
                      rounded={true}
                    />
                  </button>
                )}
              </ElipsisTooltip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantDropdown;
